import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { useClient } from '@/context/ClientContext';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { ASSESS } from '@/constants/testIds';
import { Loader2, Sparkles, Gauge } from 'lucide-react';
import { toast } from 'sonner';

export default function Assessments() {
  const { activeClient } = useClient();
  const [tab, setTab] = useState('aeo');
  const [aeoLoading, setAeoLoading] = useState(false);
  const [airLoading, setAirLoading] = useState(false);
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!activeClient) return;
    api.get(`/clients/${activeClient.client_id}/assessments`).then(r => setItems(r.data || []));
  }, [activeClient]);

  if (!activeClient) return <Empty />;

  const refresh = async () => {
    const r = await api.get(`/clients/${activeClient.client_id}/assessments`);
    setItems(r.data || []);
  };

  const runAeo = async () => {
    setAeoLoading(true);
    try {
      await api.post('/assessments/aeo', { client_id: activeClient.client_id });
      await refresh();
      toast.success('AEO assessment complete');
    } catch (e) {
      toast.error('AEO assessment failed');
    } finally {
      setAeoLoading(false);
    }
  };

  const runAir = async () => {
    setAirLoading(true);
    try {
      await api.post('/assessments/ai-readiness', { client_id: activeClient.client_id });
      await refresh();
      toast.success('AI Readiness assessment complete');
    } catch (e) {
      toast.error('AI Readiness failed');
    } finally {
      setAirLoading(false);
    }
  };

  const latestAeo = items.find(i => i.type === 'aeo');
  const latestAir = items.find(i => i.type === 'ai_readiness');

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground">Authority assessments</div>
        <h1 className="mt-2 font-display text-3xl tracking-tight">AEO & AI Readiness</h1>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="aeo">Authority Engineering (AEO)</TabsTrigger>
          <TabsTrigger value="ai_readiness">AI Readiness</TabsTrigger>
        </TabsList>

        <TabsContent value="aeo" className="mt-6">
          <div className="rounded-xl border border-border bg-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium">Authority Engineering Assessment</div>
                <div className="text-xs text-muted-foreground">Deep Reasoning performs a comprehensive AEO audit, scoring 17 authority signals.</div>
              </div>
              <Button data-testid={ASSESS.runAeo} onClick={runAeo} disabled={aeoLoading}>
                {aeoLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}
                {aeoLoading ? 'Analyzing…' : 'Run AEO Assessment'}
              </Button>
            </div>
            {latestAeo && (
              <div data-testid={ASSESS.aeoResult} className="mt-6 space-y-5">
                <AeoView result={latestAeo.result} />
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="ai_readiness" className="mt-6">
          <div className="rounded-xl border border-border bg-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium">AI Readiness Assessment</div>
                <div className="text-xs text-muted-foreground">Deep Reasoning evaluates 15 dimensions of organizational AI maturity.</div>
              </div>
              <Button data-testid={ASSESS.runAir} onClick={runAir} disabled={airLoading}>
                {airLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Gauge className="h-4 w-4 mr-1" />}
                {airLoading ? 'Analyzing…' : 'Run AI Readiness'}
              </Button>
            </div>
            {latestAir && (
              <div data-testid={ASSESS.airResult} className="mt-6 space-y-5">
                <AirView result={latestAir.result} />
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Empty() {
  return <div className="text-sm text-muted-foreground">Select or onboard a client to run assessments.</div>;
}

function AeoView({ result }) {
  if (!result) return null;
  return (
    <>
      <div className="flex items-center gap-4">
        <div className="score-num text-5xl text-primary">{result.overall_score ?? '—'}</div>
        <div>
          <Badge variant="outline">{result.letter_grade || '—'}</Badge>
          <div className="text-xs text-muted-foreground mt-1">Overall AEO score</div>
        </div>
      </div>
      <Section title="Executive summary"><p className="text-sm leading-relaxed text-muted-foreground whitespace-pre-line">{result.executive_summary}</p></Section>
      <div className="grid md:grid-cols-3 gap-4">
        <Section title="30-Day Plan"><Bullets items={result.plan_30_day} /></Section>
        <Section title="90-Day Roadmap"><Bullets items={result.roadmap_90_day} /></Section>
        <Section title="12-Month Strategy"><Bullets items={result.strategy_12_month} /></Section>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <Section title="Website Audit"><Bullets items={result.website_audit} /></Section>
        <Section title="Schema Review"><Bullets items={result.schema_review} /></Section>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <Section title="Knowledge Graph"><p className="text-sm text-muted-foreground">{result.knowledge_graph_analysis}</p></Section>
        <Section title="Entity Analysis"><p className="text-sm text-muted-foreground">{result.entity_analysis}</p></Section>
        <Section title="Content Assessment"><p className="text-sm text-muted-foreground">{result.content_assessment}</p></Section>
        <Section title="Citation Analysis"><p className="text-sm text-muted-foreground">{result.citation_analysis}</p></Section>
        <Section title="Review Analysis"><p className="text-sm text-muted-foreground">{result.review_analysis}</p></Section>
        <Section title="Local Authority"><p className="text-sm text-muted-foreground">{result.local_authority}</p></Section>
      </div>
    </>
  );
}

function AirView({ result }) {
  if (!result) return null;
  return (
    <>
      <div className="flex items-center gap-4">
        <div className="score-num text-5xl text-primary">{result.overall_score ?? '—'}</div>
        <div>
          <Badge variant="outline">{result.letter_grade || '—'}</Badge>
          <div className="text-xs text-muted-foreground mt-1">AI Readiness</div>
        </div>
      </div>
      <Section title="Executive summary"><p className="text-sm leading-relaxed text-muted-foreground whitespace-pre-line">{result.executive_summary}</p></Section>
      <Section title="Dimensions">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {Object.entries(result.dimensions || {}).map(([k, v]) => (
            <div key={k} className="rounded-md border border-border p-3">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">{k.replace(/_/g, ' ')}</div>
              <div className="score-num text-xl mt-1">{v}</div>
            </div>
          ))}
        </div>
      </Section>
      <div className="grid md:grid-cols-3 gap-4">
        <Section title="Recommendations"><Bullets items={result.recommendations} /></Section>
        <Section title="ROI Opportunities"><Bullets items={result.roi_opportunities} /></Section>
        <Section title="Quick Wins"><Bullets items={result.quick_wins} /></Section>
      </div>
      <Section title="Long-Term Strategy"><Bullets items={result.long_term_strategy} /></Section>
    </>
  );
}

function Section({ title, children }) {
  return (
    <div className="rounded-lg border border-border p-4">
      <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">{title}</div>
      {children}
    </div>
  );
}

function Bullets({ items = [] }) {
  if (!items.length) return <div className="text-xs text-muted-foreground">—</div>;
  return (
    <ul className="space-y-1.5 text-sm">
      {items.map((it, i) => (
        <li key={i} className="flex gap-2"><span className="text-primary mt-0.5">→</span><span>{it}</span></li>
      ))}
    </ul>
  );
}
